import Tooltip from 'components/design-system/Tooltip'
import Popover from '@mui/material/Popover'
import Box from '@mui/material/Box'
import NavItem from 'components/design-system/NavItem'
import Button from 'components/design-system/Button'
import Icon from 'components/design-system/Icon'
import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'react'
import config from 'config'
import { setUser } from 'store/authReducer'
import { clearAuthSession } from 'utils/authToken'

const caption12 = {
  m: 0,
  fontSize: 'var(--font-size-12)',
  fontWeight: 'var(--font-weight-regular)',
  color: 'var(--color-text-secondary)'
} // Caption/12 Regular

/** Account PopUp (Figma 25:2085): Gradient/Surface, border/default, radius/md, padding 16, gap 16, Shadow/Card. */
export default function ProfilePopover() {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const [anchorEl, setAnchorEl] = useState(null)

  const open = Boolean(anchorEl)
  const id = open ? 'profile-popover' : undefined

  const handleClose = () => setAnchorEl(null)

  const handleLogoutClick = () => {
    clearAuthSession()
    dispatch(setUser(null))
    handleClose()
  }

  return (
    <>
      <Tooltip title="Account" placement="right">
        <NavItem
          icon="account_circle"
          iconColor="var(--color-icon-accent)"
          aria-describedby={id}
          aria-label="Account"
          onClick={(event) => setAnchorEl(event.currentTarget)}
        />
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-16)',
              boxSizing: 'border-box',
              width: 220,
              ml: 'var(--space-8)',
              p: 'var(--space-16)',
              backgroundColor: 'transparent',
              backgroundImage: 'linear-gradient(to bottom, var(--color-navy-850), var(--color-navy-950))', // Gradient/Surface
              border: 'var(--stroke-default) solid var(--color-border-default)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '4px 4px 16px 0 rgba(0, 0, 0, 0.3)' // Shadow/Card
            }
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-8)', width: '100%' }}>
          <Icon name="account_circle" size={40} color="var(--color-icon-accent)" />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: '100%',
              lineHeight: 'normal',
              textAlign: 'center',
              fontFamily: 'var(--font-family-primary)',
              '& > p': { maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
            }}
          >
            <Box
              component="p"
              sx={{ m: 0, fontSize: 'var(--font-size-14)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)' }}
            >
              {user?.name}
            </Box>
            {user?.role && (
              <Box component="p" sx={caption12}>
                ({user.role})
              </Box>
            )}
            {user?.email && (
              <Box component="p" sx={caption12}>
                {user.email}
              </Box>
            )}
          </Box>
        </Box>

        {/* Button-LogOut (Figma 24:2023) */}
        <Button
          variant="secondary"
          component="a"
          href={config.homeDashboardUrl}
          aria-label="Log out and return to NBSS dashboard"
          onClick={handleLogoutClick}
          sx={{ height: 34, gap: 'var(--space-8)', bgcolor: 'transparent', borderColor: 'var(--color-bg-control)' }}
        >
          Log out
          <Icon name="sign_out" color="var(--color-icon-danger)" />
        </Button>
      </Popover>
    </>
  )
}
